import payform from 'payform';

import { CreditCard } from '@zooplus/checkout-state';

import { CreditCardNewProps } from '@components/CreditCardNew/CreditCardNew';
import { IVariant } from '@config/consts';
import {
  CardType,
  CreditCardBrand,
  CreditCardData,
  Nullable,
  cardTypeOfCreditCardBrand,
} from '@types-internal';
import { PreferredPayment } from '@types-internal/CheckoutTypes';
import { cardTypeOfCreditCardBrand as cardTypeOfCreditCardBrandCheckout } from '@types-internal/ModernizePaymentTypes';

/**
 * Wrapper of payform.validateCardNumber to validate credit card numbers
 *
 * @param cardNumber string
 * @return boolean if credit card number is valid
 */

export const isCardNumber = function (cardNumber: string) {
  return payform.validateCardNumber(cardNumber);
};

export const deleteCardArrayAndUpdate = (
  creditCards: CreditCardData[],
  cardToDelete: CreditCardData,
  newSelectedCardPspIdentifier: Nullable<string>,
) => {
  const newCreditCards = creditCards.slice();
  const deletionIndex = newCreditCards.indexOf(cardToDelete);

  if (deletionIndex >= 0) {
    newCreditCards.splice(deletionIndex, 1);
  }

  if (newSelectedCardPspIdentifier) {
    const newSelectedCardIndex = newCreditCards.findIndex(
      (card) => card.pspIdentifier === newSelectedCardPspIdentifier,
    );
    if (newSelectedCardIndex !== -1) {
      newCreditCards[newSelectedCardIndex] = {
        ...newCreditCards[newSelectedCardIndex],
        selected: true,
      };
    }
  }
  return newCreditCards;
};

export const selectCardAndUnselectPrevious = (
  currentCards: CreditCardData[],
  pspIdentifier: string | null,
) => {
  const newCreditCards = currentCards
    .slice()
    .map((c) =>
      c.pspIdentifier === pspIdentifier
        ? { ...c, selected: true }
        : c.selected
          ? { ...c, selected: false }
          : c,
    );
  return newCreditCards;
};

export function normalizeCreditCardBrand(value: unknown): Nullable<CreditCardBrand> {
  if (typeof value === 'string') {
    const maybeCreditCardBrand = value.toLowerCase();
    const cardType = (cardTypeOfCreditCardBrand as Record<string, Nullable<CardType>>)[
      maybeCreditCardBrand
    ];
    return typeof cardType !== 'undefined' ? (maybeCreditCardBrand as CreditCardBrand) : null;
  }
  return null;
}

export function convertCreditCardBrand(value: unknown): Nullable<CardType> {
  const cardType =
    typeof value === 'string'
      ? (cardTypeOfCreditCardBrand as Record<string, Nullable<CardType>>)[value.toLowerCase()]
      : null;
  return cardType === undefined ? null : cardType;
}

/**
 * Convert string to a format that there's space between every 4 characters
 * <p>For example: DE12444 to DE12 4444
 *
 * @param text Iban number or credit card number
 * @return string in new format
 */

export const checkBancontactCardNumber = (cardNumber: string) => {
  if (cardNumber !== '' && cardNumber.length < 19) {
    return false;
  } else if (!cardNumber.match(/^(6703|4871)/)) {
    return true;
  }
  return true;
};
/**
 * Convert string to a format that there's space between every 4 characters
 * <p>For example: DE12444 to DE12 4444
 *
 * @param text Iban number or credit card number
 * @param onlyNumbers in case of credit card number it should return only numbers
 * @return string in new format
 */

export const formatWithSpaces = (text: string, onlyNumbers?: boolean) => {
  const inputNumbersOnly = text.replace(onlyNumbers ? /\D/gi : /\W/gi, '');
  const splits = inputNumbersOnly.match(/.{1,4}/g);

  let spacedNumber = '';
  if (splits) {
    spacedNumber = splits.join(' ');
  }

  return spacedNumber;
};

/**
 * Prevents the cursor within an input that uses `formatWithSpaces` from jumping to the end
 * if the user puts the cursor in the middle and starts typing from there.
 *
 * @param target Event target
 * @return string formatted value
 */

export const persistCursorPosForSpacedInputValue = (
  target: EventTarget & Pick<HTMLInputElement, 'value' | 'selectionStart' | 'selectionEnd'>,
  onlyNumbers?: boolean,
) => {
  const value = formatWithSpaces(target.value, onlyNumbers);
  let position = target.selectionStart || 0;
  if (target.value.length !== value.length && position === target.value.length) {
    position = value.length;
  }
  target.value = value;
  target.selectionEnd = position;
  return value;
};

/**
 * Convert string to accept only numbers
 *
 * @param text any string
 *
 * @return string with only numbers
 */

export const acceptOnlyNumber = (text: string) => {
  return text.replace(/\D/gi, '');
};

/**
 * Checks if the string only contains non numeric characters
 *
 * @param text any string
 *
 * @return boolean if the string is valid
 */
export const isNonNumeric = (text: string) => {
  return /^[A-Za-z]+( [A-Za-z]+){0,2}$/.test(text);
};

/**
 * Check whether provided date(month, year) is valid
 *
 * @param month 2 digit string from 01 to 12
 * @param year 2 digit string
 *
 * @return boolean to show whether date is valid
 */
export const validateDate = (month: string, year: string) => {
  if (!month || !year || ['00', '0'].includes(month) || ['00', '0'].includes(year)) return false;
  const curDate = new Date();

  const expiryDate = new Date(
    year.length === 2 ? Number('20' + year) : Number(year),
    Number(month),
  );
  const maxFutureDate = new Date(curDate.getFullYear() + 15, 12);

  expiryDate.setMonth(expiryDate.getMonth() - 1);
  expiryDate.setMonth(expiryDate.getMonth() + 1, 1);
  return expiryDate > curDate && expiryDate <= maxFutureDate;
};

/**
 * Check whether provided IBAN is valid
 * based on the official algorithm for IBAN validation https://en.wikipedia.org/wiki/International_Bank_Account_Number#Algorithms
 * @param iban string representing the IBAN input value
 *
 * @return boolean to show whether IBAN is valid
 */

export const isIbanValid = (iban: string) => {
  const ibanStripped = iban
    .replace(/[^A-Z0-9]+/gi, '') //keep numbers and letters only
    .toUpperCase(); //calculation expects upper-case

  const m = ibanStripped.match(/^([A-Z]{2})([0-9]{2})([A-Z0-9]{9,30})$/);
  if (!m) return false;

  const numbericed = (m[3] + m[1] + m[2]).replaceAll(/[A-Z]/g, (ch: string) => {
    //replace upper-case characters by numbers 10 to 35
    return `${ch.charCodeAt(0) - 55}`;
  });
  //The resulting number would be to long for javascript to handle without loosing precision.
  //So the trick is to chop the string up in smaller parts.
  const mod97 = numbericed.match(/\d{1,7}/g)?.reduce((total: string, curr: string) => {
    return `${Number(total + curr) % 97}`;
  }, '');

  return Number(mod97) === 1;
};

/**
 * Get the last 4 digits of an IBAN
 * @param iban string representing the IBAN input value
 *
 * @return string last 4 digits of the IBAN
 */
export const getIBANLastFourDigits = (iban?: Nullable<string>) => {
  if (!iban) return '';
  const ibanStripped = iban
    ? iban.replace(/[^A-Z0-9]+/gi, '') //keep numbers and letters only
    : '';
  return ibanStripped.substring(ibanStripped.length - 4);
};

/**
 * Converts CreditCard type comes from checkout to CreditCardData type
 * @param source single credit card data in CreditCard type
 *
 * @return single credit card data in CreditCardData type
 */

export const mapCreditCardtoCreditCardData = (source: CreditCard) => {
  return {
    pspIdentifier: source.pspIdentifier,
    psp: source.pspIdentifier,
    holderName: source.holderName,
    brand: source.brand && cardTypeOfCreditCardBrandCheckout[source.brand],
    expirationMonth: source.expirationMonth,
    expirationYear: source.expirationYear,
    expired: source.expired,
    lastFourDigits: source.maskedNumber,
    nonce: source.nonce,
  } as CreditCardData;
};

/**
 * Returns sepcific payment method based on paymentMethodShortName
 *
 * @param paymentMethods - the list of payment methods available
 * @param paymentMethodShortName - the short name of the payment method to find
 *
 * @return A string representing card unique identifier or empty if selected payment isn't saved card
 */
export const getSelectedCardIdentifier = (preferredPayment: PreferredPayment | undefined) => {
  if (
    preferredPayment?.paymentMethodShortName &&
    ['K', 'BC'].includes(preferredPayment?.paymentMethodShortName)
  ) {
    return preferredPayment?.details?.cards?.[0]?.pspIdentifier || '';
  }
  return '';
};

/**
 * Returns a specific data-testid string for the stored credit card wrapper based on its type and state
 *
 * @param creditCard - The credit card object containing selection and state details
 * @param isSessionCard - Boolean indicating if the card is a temporary session card
 * @param isBancontactCard - Boolean indicating if the card is a Bancontact card
 *
 * @return A string representing the corresponding test ID for the card's wrapper
 */

export const getDataTestId = (
  creditCard: CreditCardNewProps['creditCard'],
  isSessionCard: boolean,
  isBancontactCard: boolean,
) => {
  if (isBancontactCard) {
    return 'storedBCWrapper';
  }
  if (isSessionCard) {
    return 'storedSessionCC';
  }
  if (creditCard.selected) {
    return 'storedSelectedCC';
  }
  return 'storedCC';
};

/**
 * Determines the visual variant to be applied to a credit card component based on its state
 *
 * @param creditCard - The credit card object containing information about selection and expiration
 *
 * @return A variant string ('error', 'selected', or 'default') to be used for UI styling
 */

export const getVariant = (creditCard: CreditCardNewProps['creditCard']): IVariant => {
  if (creditCard.expired) {
    return 'error';
  }
  if (creditCard.selected) {
    return 'selected';
  }
  return 'default';
};

/**
 * Returns the appropriate card icon type based on the card's brand or if it's a Bancontact card
 *
 * @param isBancontactCard - Boolean indicating if the card is a Bancontact card
 * @param creditCard - The credit card object containing brand information
 * @param cardTypeOfCreditCardBrand - A mapping of credit card brands to card icon types
 *
 * @return A string representing the icon type (e.g., 'visa', 'mastercard', 'bcmc') or undefined if not applicable
 */

export function getCardIconType(
  isBancontactCard: boolean,
  creditCard: CreditCardNewProps['creditCard'],
) {
  if (isBancontactCard) {
    return 'bcmc';
  } else if (creditCard.brand) {
    return cardTypeOfCreditCardBrandCheckout[creditCard.brand];
  } else {
    return undefined;
  }
}
